// Caches to avoid duplicate fetches.
const mappingCache = {}; // key: bundled JS file URL, value: Promise resolving to { mappings, sources, names, sourcesContent, sourceRoot, mapUrl }.

// Base64 VLQ decoding.
const base64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const base64Map = {};
for (let i = 0; i < base64Chars.length; i++) {
	base64Map[base64Chars[i]] = i;
}

function decodeVLQ (str, index) {
	let result = 0;
	let shift = 0;
	let digit;
	let continuation;
	do {
		digit = base64Map[str.charAt(index++)];
		continuation = digit & 32; // 6th bit indicates continuation.
		digit &= 31; // lower 5 bits.
		result += digit << shift;
		shift += 5;
	} while (continuation);
	const sign = result & 1 ? -1 : 1;
	result = result >> 1;
	return { value: result * sign, nextIndex: index };
}

// Parse the source map's mappings string.
function parseMappings (mappingsStr, sources, names) {
	const lines = mappingsStr.split(';');
	const mappings = [];
	let prevGeneratedColumn = 0;
	let prevSource = 0;
	let prevOriginalLine = 0;
	let prevOriginalColumn = 0;
	let prevName = 0;

	for (let generatedLine = 0; generatedLine < lines.length; generatedLine++) {
		const line = lines[generatedLine];
		prevGeneratedColumn = 0;
		if (!line) {
			continue;
		}

		const segments = line.split(',');
		for (const segment of segments) {
			if (!segment) {
				continue;
			}

			let index = 0;
			let decoded = decodeVLQ(segment, index);
			const generatedColumn = prevGeneratedColumn + decoded.value;
			index = decoded.nextIndex;
			prevGeneratedColumn = generatedColumn;

			const mapping = { generatedLine, generatedColumn };

			// If additional fields are present, decode them.
			if (index < segment.length) {
				decoded = decodeVLQ(segment, index);
				const sourceIndex = prevSource + decoded.value;
				index = decoded.nextIndex;
				prevSource = sourceIndex;

				mapping.sourceIndex = sourceIndex; // <-- keep index so we can use sourcesContent
				mapping.source = sources[sourceIndex]; // original string, for display

				decoded = decodeVLQ(segment, index);
				const originalLine = prevOriginalLine + decoded.value;
				index = decoded.nextIndex;
				prevOriginalLine = originalLine;
				mapping.originalLine = originalLine;

				decoded = decodeVLQ(segment, index);
				const originalColumn = prevOriginalColumn + decoded.value;
				index = decoded.nextIndex;
				prevOriginalColumn = originalColumn;
				mapping.originalColumn = originalColumn;

				if (index < segment.length) {
					decoded = decodeVLQ(segment, index);
					const nameIndex = prevName + decoded.value;
					index = decoded.nextIndex;
					prevName = nameIndex;
					mapping.name = names[nameIndex];
				}
			}

			mappings.push(mapping);
		}
	}
	return mappings;
}

function findMappingFor (mappings, line, column) {
	let candidate = null;
	for (const mapping of mappings) {
		if (mapping.generatedLine === line && mapping.generatedColumn <= column) {
			if (!candidate || mapping.generatedColumn > candidate.generatedColumn) {
				candidate = mapping;
			}
		}
	}
	return candidate;
}

// Remove any ":formatted:" marker (but DO NOT strip ../ anymore for sources!)
function preprocessFilePath (filePath) {
	return filePath.replace(/:formatted:/, ':');
}

function extractSourceMapUrl (jsContent) {
	const regex = /\/\/[#@]\s*sourceMappingURL=(\S+)/;
	const match = jsContent.match(regex);
	return match ? match[1] : null;
}

function fetchSourceMapUrlFromScript (scriptUrl) {
	return fetch(scriptUrl)
		.then((response) => {
			if (!response.ok) {
				throw new Error('Unable to fetch JS file: ' + scriptUrl);
			}
			return response.text();
		})
		.then((text) => {
			const mapUrl = extractSourceMapUrl(text);
			if (!mapUrl) {
				throw new Error('No sourceMappingURL found in ' + scriptUrl);
			}
			return new URL(mapUrl, scriptUrl).toString();
		});
}

// Return sourcemap context, not just mappings.
function getMapContextForFile (fileUrl) {
	if (mappingCache[fileUrl]) {
		return mappingCache[fileUrl];
	}

	const promise = fetchSourceMapUrlFromScript(fileUrl)
		.then(sourceMapUrl =>
			fetch(sourceMapUrl).then((response) => {
				if (!response.ok) {
					throw new Error('Unable to fetch source map for ' + fileUrl);
				}
				return response.json().then(sourceMap => ({ sourceMap, sourceMapUrl }));
			})
		)
		.then(({ sourceMap, sourceMapUrl }) => {
			const sources = sourceMap.sources || [];
			const names = sourceMap.names || [];
			const mappings = parseMappings(sourceMap.mappings, sources, names);

			return {
				mappings,
				sources,
				names,
				sourcesContent: sourceMap.sourcesContent || null,
				sourceRoot: sourceMap.sourceRoot || '',
				mapUrl: sourceMapUrl
			};
		})
		.catch((err) => {
			console.error(err);
			return null;
		});

	mappingCache[fileUrl] = promise;
	return promise;
}

// Helper: pull a line from sourcesContent safely
function getSnippetFromSourcesContent (sourcesContent, sourceIndex, oneBasedLine) {
	if (!sourcesContent || !Array.isArray(sourcesContent)) {
		return '';
	}
	const content = sourcesContent[sourceIndex];
	if (typeof content !== 'string') {
		return '';
	}
	const lines = content.split(/\r?\n/);
	return (lines[oneBasedLine - 1] || '').trim();
}

// Stack parsing (unchanged)
function parseStackLine (line) {
	line = line.trim();
	let callee;
	let fileLineColumn = [];
	let native = false;
	let planA;
	let planB;

	if (
		(planA = line.match(/at (.+) \(eval at .+ \((.+)\), .+\)/))
		|| (planA = line.match(/at (.+) \((.+)\)/))
		|| ((line.slice(0, 3) !== 'at ') && (planA = line.match(/(.*)@(.*)/)))
	) {
		callee = planA[1];
		native = (planA[2] === 'native');
		fileLineColumn = (planA[2].match(/(.*):(\d+):(\d+)/) || planA[2].match(/(.*):(\d+)/) || []).slice(1);
	} else if ((planB = line.match(/^(at\s+)*(.+):(\d+):(\d+)/))) {
		fileLineColumn = planB.slice(2);
	} else {
		return undefined;
	}

	if (callee && !fileLineColumn[0]) {
		const type = callee.split('.')[0];
		if (type === 'Array') {
			native = true;
		}
	}

	return {
		beforeParse: line,
		callee: callee || '',
		index: typeof window !== 'undefined' && (fileLineColumn[0] === window.location.href),
		native: native || false,
		file: fileLineColumn[0] ? preprocessFilePath(fileLineColumn[0]) : '',
		line: parseInt(fileLineColumn[1] || '', 10) || undefined,
		column: parseInt(fileLineColumn[2] || '', 10) || undefined
	};
}

async function transformStack (stack) {
	const formattedLines = [];
	const items = [];

	const entries = stack
		.split('\n')
		.map(line => parseStackLine(line))
		.filter(x => x !== undefined);

	// Keep order stable by mapping to results array
	const results = await Promise.all(entries.map(async (entry) => {
		if (entry.file && entry.line && entry.column) {
			const filePath = entry.file.replace(/^@/, '');
			const generatedLineNumber = entry.line;
			const generatedColumnNumber = entry.column;

			const ctx = await getMapContextForFile(filePath);
			if (ctx?.mappings) {
				const mapping = findMappingFor(ctx.mappings, generatedLineNumber - 1, generatedColumnNumber - 1);
				if (mapping && typeof mapping.sourceIndex === 'number') {
					const originalLine = (mapping.originalLine ?? 0) + 1;
					const originalColumn = (mapping.originalColumn ?? 0) + 1;
					const functionName = mapping.name || entry.callee || '';
					const mappedFile = preprocessFilePath(mapping.source || '');

					// Snippet ONLY from sourcesContent (no fetch)
					const snippet = getSnippetFromSourcesContent(ctx.sourcesContent, mapping.sourceIndex, originalLine);

					const fnPadded = functionName.padEnd(6, ' ');
					const locationStr = `${mappedFile}:${originalLine}:${originalColumn}`;
					const formattedLine = snippet
						? `at ${fnPadded} ${locationStr}      ${snippet}`
						: `at ${fnPadded} ${locationStr}`;

					items.push({
						fileName: mappedFile,
						lineNumber: originalLine,
						columnNumber: originalColumn,
						originalCode: snippet
					});

					return formattedLine;
				}
			}
		}
		return entry.beforeParse;
	}));

	formattedLines.push(...results);

	return {
		stackFrameParsed: formattedLines.join('\n'),
		items
	};
}

export function mapErrorStack (error) {
	if (!error || !error.stack) {
		return Promise.reject(new Error('Invalid error object; missing stack property.'));
	}
	return transformStack(error.stack);
}
